import React, { useState } from 'react';
import { Card, Button, Alert, Form, Row, Col } from 'react-bootstrap';

const ATOValidation = () => {
  const [validationData, setValidationData] = useState({
    abn: '',
    gst_sales: '',
    gst_purchases: '',
    gst_payable: '',
    period_start: '',
    period_end: ''
  });
  
  const [validationResult, setValidationResult] = useState(null);
  const [isValidating, setIsValidating] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValidationData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleValidation = async () => {
    setIsValidating(true);
    setValidationResult(null);

    try {
      const response = await fetch('/api/validate-ato', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(validationData),
      });

      const result = await response.json();
      setValidationResult(result);
    } catch (error) {
      setValidationResult({
        valid: false,
        errors: ['Validation failed: ' + error.message],
        warnings: []
      });
    } finally {
      setIsValidating(false);
    }
  };

  const clearValidation = () => {
    setValidationData({
      abn: '',
      gst_sales: '',
      gst_purchases: '',
      gst_payable: '',
      period_start: '',
      period_end: ''
    });
    setValidationResult(null);
  };

  return (
    <div className="form-section">
      <h3>
        <i className="fas fa-check-circle me-2"></i>
        ATO Compliance Validation
      </h3>
      
      <Card className="mb-4">
        <Card.Header>Validation Form</Card.Header>
        <Card.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>ABN</Form.Label>
                  <Form.Control
                    type="text"
                    name="abn"
                    value={validationData.abn}
                    onChange={handleChange}
                    placeholder="11 digit ABN"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Period Start</Form.Label>
                  <Form.Control
                    type="date"
                    name="period_start"
                    value={validationData.period_start}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Period End</Form.Label>
                  <Form.Control
                    type="date"
                    name="period_end"
                    value={validationData.period_end}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>GST Sales ($)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="gst_sales"
                    value={validationData.gst_sales}
                    onChange={handleChange}
                    placeholder="0.00"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>GST Purchases ($)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="gst_purchases"
                    value={validationData.gst_purchases}
                    onChange={handleChange}
                    placeholder="0.00"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>GST Payable ($)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="gst_payable"
                    value={validationData.gst_payable}
                    onChange={handleChange}
                    placeholder="0.00"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
          
          <div className="d-flex justify-content-between mt-3">
            <Button
              variant="outline-secondary"
              onClick={clearValidation}
              disabled={isValidating}
            >
              <i className="fas fa-times me-1"></i>
              Clear
            </Button>
            
            <Button
              variant="primary"
              onClick={handleValidation}
              disabled={isValidating}
            >
              {isValidating ? (
                <>
                  <i className="fas fa-spinner fa-spin me-1"></i>
                  Validating...
                </>
              ) : (
                <>
                  <i className="fas fa-check me-1"></i>
                  Validate ATO Compliance
                </>
              )}
            </Button>
          </div>
        </Card.Body>
      </Card>

      {validationResult && (
        <Card>
          <Card.Header>Validation Results</Card.Header>
          <Card.Body>
            {validationResult.valid ? (
              <Alert variant="success">
                <i className="fas fa-check-circle me-2"></i>
                <strong>Validation Passed!</strong>
                <br />
                Your form data appears to be compliant with ATO requirements.
              </Alert>
            ) : (
              <Alert variant="danger">
                <i className="fas fa-exclamation-triangle me-2"></i>
                <strong>Validation Failed!</strong>
                <br />
                Please review and fix the following issues:
              </Alert>
            )}

            {validationResult.errors && validationResult.errors.length > 0 && (
              <div className="mb-3">
                <h6 className="validation-error">
                  <i className="fas fa-times-circle me-1"></i>
                  Errors:
                </h6>
                <ul className="validation-error">
                  {validationResult.errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            {validationResult.warnings && validationResult.warnings.length > 0 && (
              <div className="mb-3">
                <h6 className="validation-warning">
                  <i className="fas fa-exclamation-triangle me-1"></i>
                  Warnings:
                </h6>
                <ul className="validation-warning">
                  {validationResult.warnings.map((warning, index) => (
                    <li key={index}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}

            {validationResult.valid && validationResult.warnings && validationResult.warnings.length === 0 && (
              <div className="validation-result">
                <Alert variant="success">
                  <i className="fas fa-thumbs-up me-2"></i>
                  No issues found. Your form is ready for ATO submission.
                </Alert>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      <Card className="mt-4">
        <Card.Header>ATO Compliance Guidelines</Card.Header>
        <Card.Body>
          <h6>Key Validation Rules:</h6>
          <ul>
            <li><strong>ABN Format:</strong> Must be 11 digits with valid checksum</li>
            <li><strong>Date Range:</strong> End date must be after start date</li>
            <li><strong>GST Calculations:</strong> GST payable should equal (GST Sales - GST Purchases) × 10%</li>
            <li><strong>Period Validation:</strong> Must align with ATO reporting periods (quarterly)</li>
            <li><strong>Amount Validation:</strong> All monetary values must be positive</li>
          </ul>
          
          <h6>Common Issues:</h6>
          <ul>
            <li>Incorrect ABN format or checksum</li>
            <li>GST calculations not matching expected formulas</li>
            <li>Invalid date ranges or periods</li>
            <li>Missing required fields</li>
            <li>Negative values where not allowed</li>
          </ul>
        </Card.Body>
      </Card>
    </div>
  );
};

export default ATOValidation;
